import { useFormik } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Button, ModalBody, ModalFooter, ModalHeader, Input } from 'reactstrap';
import * as Yup from 'yup';
import { departmentType, gender, source, visitReason } from '../constants';
import {
  displayError,
  displaySuccess,
  isAge18orAbove,
  logAmplitudeEvent
} from '../utilities';
import DynamicSelect from './DynamicSelect';
import { createUserV2, GET_ALL_HOSPITALS } from '../queries';
import { useQuery } from 'react-apollo';

export default function MpidRegistration(props) {
  const { data: { hospitals = [] } = {} } = useQuery(GET_ALL_HOSPITALS, {
    variables: { showAll: false }
  });

  const formattedHospitals = hospitals.map(hospital => ({
    label: hospital.name,
    value: hospital.id
  }));

  const userDetails = JSON.parse(localStorage.getItem('user'));
  const userAllotcatedHospital =
    userDetails.hospitals.length === 0
      ? formattedHospitals
      : hospitals
          .filter(hospital => userDetails.hospitals.includes(hospital.id))
          .map(hospital => ({
            value: hospital.id,
            label: hospital.name
          }));

  const initialValues = {
    visitPurpose: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    mobile: '',
    email: '',
    addressStreet: '',
    addressCity: '',
    pinCode: '',
    forceCreate: false,
    departmentType: '',
    edd: '',
    visitReason: '',
    source: '',
    remarks: '',
    vcReferral: '',
    hospitalId: null
  };
  const validationSchema = Yup.object().shape({
    visitPurpose: Yup.string(),
    first_name: Yup.string().required('Please enter the first name.'),
    date_of_birth: Yup.string().required('Please enter the date of birth'),
    gender: Yup.string().required('Please enter the gender'),
    mobile: Yup.string()
      .required('Please enter the mobile number')
      .length(10, 'Mobile number must be 10 digits')
      .matches(/^(12345[0-9]{5}|[6-9][0-9]{9})$/, 'Invalid mobile number'),
    email: Yup.string()
      .required('Please enter the email address')
      .email('Invalid Email address'),
    addressStreet: Yup.string(),
    addressCity: Yup.string(),
    pinCode: Yup.string().matches(/^[1-9][0-9]{5}$/, 'Invalid pincode.'),
    forceCreate: Yup.boolean().required(),
    departmentType: Yup.string(),
    edd: Yup.string()
      .nullable()
      .test('edd-date-range', 'Please enter a valid EDD', function(value) {
        if (!value) return true;

        const selectedDate = moment(value).startOf('day');
        const today = moment().startOf('day');
        const maxDate = today.clone().add(280, 'days');

        if (selectedDate.isBefore(today)) {
          return this.createError({ message: 'EDD cannot be in the past' });
        }
        if (selectedDate.isAfter(maxDate)) {
          return this.createError({
            message: 'EDD cannot be more than 280 days from today'
          });
        }
        return true;
      })
      .test(
        'female-min-age-18',
        'EDD can only be entered if the age is 18 years or above',
        function(value) {
          const { gender, date_of_birth } = this.parent;
          if (!value) return true;

          if (gender !== 'Female') return true;

          if (!date_of_birth) return true;

          const age = moment().diff(moment(date_of_birth), 'years');

          return age >= 18;
        }
      ),
    visitReason: Yup.string(),
    source: Yup.string(),
    remarks: Yup.string(),
    vcReferral: Yup.string().max(100, 'Maximum 100 characters allowed'),
    hospitalId: Yup.number()
      .nullable()
      .when('edd', {
        is: edd => !!edd,
        then: schema =>
          schema.required('Hospital is required when EDD is selected'),
        otherwise: schema => schema.notRequired()
      })
  });

  const onSubmit = async (values, actions) => {
    logAmplitudeEvent(
      'Registration_Icon_Pressed',
      {
        mobile: values.mobile
      },
      true
    );

    const input = {
      dob: moment(values.date_of_birth).format('YYYY-MM-DD'),
      firstName: values.first_name,
      lastName: values.last_name || undefined,
      mobile: values.mobile.toString(),
      siteCode: props.selectedHospitalDetails.siteCode,
      gender: values.gender,
      email: values.email,
      onlyMpid: props.registeredCustomers.data.length > 0,
      departmentType: values.departmentType,
      edd: values.edd,
      visitReason: values.visitReason,
      source: values.source,
      remarks: values.remarks,
      vcReferral: values.vcReferral,
      pincode: String(values.pinCode),
      hospitalId: values.hospitalId || null
    };

    try {
      const resData = await createUserV2(input);
      actions.resetForm();

      if (
        (resData.data && resData.data.errors) ||
        (resData.errors && resData.errors.length !== 0)
      ) {
        displayError('Something went wrong while creating the user.');
        return;
      }

      displaySuccess('MPID Registered');
      props.toggleListPage();
    } catch (err) {
      actions.setSubmitting(false);
      displayError('Something went wrong!');
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema
  });

  useEffect(() => {
    if (userAllotcatedHospital.length === 1 && !formik.values.hospitalId) {
      formik.setFieldValue('hospitalId', userAllotcatedHospital[0].value);
    }
  }, [userAllotcatedHospital]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <ModalHeader toggle={props.toggle}>Registration Details</ModalHeader>
      <ModalBody>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'auto minmax(120px, 1fr) auto minmax(120px, 1fr)',
            columnGap: '16px',
            rowGap: '12px',
            alignItems: 'center',
            marginTop: 12
          }}
        >
          <label style={{ margin: '0px' }}>
            First Name: <span style={{ fontSize: '1em', color: 'red' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              className="form-control"
              name="first_name"
              onChange={formik.handleChange}
              value={formik.values.first_name}
              onBlur={formik.handleBlur}
            />
            {formik.touched.first_name && formik.errors.first_name ? (
              <span style={{ color: 'red' }}>{formik.errors.first_name}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>Last Name:</label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              className="form-control"
              name="last_name"
              onChange={formik.handleChange}
              value={formik.values.last_name}
              onBlur={formik.handleBlur}
            />
          </div>

          <label style={{ margin: '0px' }}>Address:</label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              className="form-control"
              name="addressStreet"
              value={formik.values.addressStreet}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.addressStreet && formik.errors.addressStreet ? (
              <span style={{ color: 'red' }}>
                {formik.errors.addressStreet}
              </span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>Pincode:</label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="number"
              className="form-control"
              name="pinCode"
              onChange={formik.handleChange}
              value={formik.values.pinCode}
              onBlur={formik.handleBlur}
            />
            {formik.touched.pinCode && formik.errors.pinCode ? (
              <span style={{ color: 'red' }}>{formik.errors.pinCode}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>City:</label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              className="form-control"
              name="addressCity"
              value={formik.values.addressCity}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.addressCity && formik.errors.addressCity ? (
              <span style={{ color: 'red' }}>{formik.errors.addressCity}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>
            Mobile: <span style={{ fontSize: '1em', color: 'red' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <h6 style={{ margin: '0px 4px 0px 0px' }}>+91 </h6>
              <input
                type="number"
                className="form-control"
                maxLength="10"
                name="mobile"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
            {formik.touched.mobile && formik.errors.mobile ? (
              <span style={{ color: 'red' }}>{formik.errors.mobile}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>
            Gender: <span style={{ fontSize: '1em', color: 'red' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <select
              className="form-control"
              name="gender"
              value={formik.values.gender}
              onChange={event => {
                const gender = event.target.value;
                formik.setFieldValue('gender', gender);
                if (
                  gender === 'Male' ||
                  (gender === 'Female' &&
                    !isAge18orAbove(formik.values.date_of_birth))
                ) {
                  formik.setFieldValue('edd', '');
                  formik.setFieldValue('hospitalId', null);
                }
              }}
              onBlur={formik.handleBlur}
            >
              <option value="">Select</option>
              {gender.map((gen, i) => {
                return (
                  <option gender={gen.value} name="gender" key={i}>
                    {gen.label}
                  </option>
                );
              })}
            </select>
            {formik.touched.gender && formik.errors.gender ? (
              <span style={{ color: 'red' }}>{formik.errors.gender}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>
            Date Of Birth:{' '}
            <span style={{ fontSize: '1em', color: 'red' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="date"
              className="form-control"
              name="date_of_birth"
              max={moment().format('YYYY-MM-DD')}
              onChange={event => {
                const dob = event.target.value;
                formik.setFieldValue('date_of_birth', dob);
                if (formik.values.gender === 'Female' && !isAge18orAbove(dob)) {
                  formik.setFieldValue('edd', '');
                  formik.setFieldValue('hospitalId', null);
                }
              }}
              value={formik.values.date_of_birth}
              onBlur={formik.handleBlur}
            />
            {formik.touched.date_of_birth && formik.errors.date_of_birth ? (
              <span style={{ color: 'red' }}>
                {formik.errors.date_of_birth}
              </span>
            ) : null}
          </div>

          <label htmlFor="edd" style={{ margin: '0px' }}>
            EDD:
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Input
              id="edd"
              name="edd"
              placeholder="EDD"
              type="date"
              value={formik.values.edd}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={
                formik.values.gender === 'Male' ||
                (formik.values.gender === 'Female' &&
                  moment().diff(moment(formik.values.date_of_birth), 'years') <
                    18)
              }
            />
            {formik.touched.edd && formik.errors.edd ? (
              <span style={{ color: 'red' }}>{formik.errors.edd}</span>
            ) : null}
          </div>

          <label htmlFor="edd" style={{ margin: '0px' }}>
            Hospital:
            {formik.values.edd && (
              <span style={{ fontSize: '1em', color: 'red' }}> *</span>
            )}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <DynamicSelect
              name="hospitalId"
              value={formik.values.hospitalId}
              isDisabled={
                !formik.values.edd || userAllotcatedHospital.length === 1
              }
              options={userAllotcatedHospital}
              onChange={(field, value) => formik.setFieldValue(field, value)}
              width="100%"
            />
            {formik.touched.hospitalId && formik.errors.hospitalId ? (
              <span style={{ color: 'red' }}>{formik.errors.hospitalId}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>Purpose of Visit:</label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <select
              className="form-control"
              value={formik.values.visitPurpose}
              name="visitPurpose"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select</option>
              <option service="Obstetrics" name="service">
                PREGNANCY
              </option>
              <option service="Others" name="service">
                WOMEN HEALTH
              </option>
              <option service="Fertility" name="service">
                FERTILITY
              </option>
              <option service="Pediatrics" name="service">
                PEDIATRICS
              </option>
              <option service="Obstetrics" name="service">
                OBSTETRICS
              </option>
              <option service="Gynecology" name="service">
                GYNECOLOGY
              </option>
              <option service="Cosmetology" name="service">
                COSMETOLOGY
              </option>
              <option service="Obstetrics" name="service">
                PREGNANCY SERVICES
              </option>
              <option service="Others" name="service">
                OTHERS
              </option>
            </select>
            {formik.touched.visitPurpose && formik.errors.visitPurpose ? (
              <span style={{ color: 'red' }}>{formik.errors.visitPurpose}</span>
            ) : null}
          </div>

          <label style={{ margin: '0px' }}>
            Email: <span style={{ fontSize: '1em', color: 'red' }}>*</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              className="form-control"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email ? (
              <span style={{ color: 'red' }}>{formik.errors.email}</span>
            ) : null}
          </div>
        </div>
      </ModalBody>
      <hr style={{ margin: '0px' }} />
      <ModalBody>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '12px',
            justifyContent: 'space-between'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px'
            }}
          >
            <label htmlFor="departmentType" style={{ margin: 0 }}>
              Type:
            </label>
            <DynamicSelect
              name="departmentType"
              value={formik.values.departmentType}
              options={departmentType}
              onChange={(field, value) => formik.setFieldValue(field, value)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'space-between'
            }}
          >
            <label htmlFor="visitReason" style={{ margin: 0 }}>
              Visit Reason:
            </label>

            <DynamicSelect
              name="visitReason"
              value={formik.values.visitReason}
              options={
                (formik.values.departmentType &&
                  visitReason.find(f => {
                    return formik.values.departmentType === f.type;
                  }).options) ||
                []
              }
              onChange={(field, value) => formik.setFieldValue(field, value)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'space-between'
            }}
          >
            <label htmlFor="source" style={{ margin: 0 }}>
              Source:
            </label>
            <DynamicSelect
              name="source"
              value={formik.values.source}
              options={source}
              onChange={(field, value) => formik.setFieldValue(field, value)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'space-between'
            }}
          >
            <label htmlFor="remarks" style={{ margin: 0 }}>
              Remarks:
            </label>
            <Input
              style={{ width: '70%' }}
              type="text"
              value={formik.values.remarks}
              onChange={e => {
                formik.setFieldValue('remarks', e.target.value);
              }}
            />
          </div>
          {formik.values.source === 'Doctor Referral - Visiting Consultant' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'space-between'
              }}
            >
              <label htmlFor="vcReferral" style={{ margin: 0 }}>
                VC Referral:
              </label>

              <Input
                style={{ width: '70%' }}
                type="text"
                value={formik.values.vcReferral}
                onChange={e => {
                  formik.setFieldValue('vcReferral', e.target.value);
                }}
              />
              {formik.touched.vcReferral && formik.errors.vcReferral ? (
                <span style={{ color: 'red' }}>{formik.errors.vcReferral}</span>
              ) : null}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter
        style={{
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center'
            }}
          >
            <input
              type="checkbox"
              id="forceCreate"
              name="forceCreate"
              checked={formik.values.forceCreate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <label
              style={{
                fontSize: '14px',
                margin: '0px 6px'
              }}
              htmlFor="forceCreate"
            >
              <i>Force create</i>
            </label>
          </div>
          <div>
            {formik.touched.forceCreate && formik.errors.forceCreate ? (
              <span style={{ color: 'red' }}>{formik.errors.forceCreate}</span>
            ) : null}
          </div>
        </div>
        <div
          style={{
            width: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '6px'
          }}
        >
          <Button color="secondary" onClick={props.toggleListPage}>
            Back
          </Button>
          <Button color="secondary" onClick={props.toggle}>
            Close
          </Button>
          <Button color="primary" type="submit" disabled={formik.isSubmitting}>
            Register
          </Button>
        </div>
      </ModalFooter>
    </form>
  );
}

MpidRegistration.propTypes = {
  selectedHospitalDetails: PropTypes.object,
  registeredCustomers: PropTypes.object,
  toggleListPage: PropTypes.func,
  toggle: PropTypes.func
};
